/**
 * Generic client-side table export — the shared plumbing behind every
 * list "Export" button (vendors, bills, …). A caller supplies a typed
 * column spec + rows; this module serialises to CSV or a styled `.xlsx`
 * and streams the file out. PDF is rendered server-side (Gotenberg) and
 * only the base64 → download step lives here (`downloadBase64Pdf`).
 *
 * ExcelJS is lazy-loaded so the ~700 KB library only lands in the bundle
 * when an operator actually exports to Excel (mirrors the customer
 * statement export).
 */

/** Excel number format — Indian lakh grouping with a ₹ prefix. */
export const INR_EXCEL_FORMAT = '"₹"#,##,##0.00;"-₹"#,##,##0.00';

export interface ExportColumn<T> {
  header: string;
  /** String value for a CSV cell. */
  csv: (row: T) => string;
  /** Native value for an Excel cell — number for money/counts, else string. */
  xlsx: (row: T) => string | number;
  /** Money column → rendered as a ₹-formatted number in Excel. */
  money?: boolean;
}

/** RFC 4180 cell escaping — quote when the value has a comma, quote, or newline. */
function csvCell(value: string): string {
  return /[",\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

function triggerDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/** Build + download a CSV. No external dependency; UTF-8 BOM so Excel opens ₹ correctly. */
export function downloadTableCsv<T>(
  columns: ExportColumn<T>[],
  rows: T[],
  fileName: string,
): void {
  const lines = [
    columns.map((c) => csvCell(c.header)).join(","),
    ...rows.map((r) => columns.map((c) => csvCell(c.csv(r))).join(",")),
  ];
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  triggerDownload(blob, fileName);
}

export interface XlsxOptions<T> {
  sheetName: string;
  fileName: string;
  /** Rows for which the cells are greyed (e.g. archived / void). */
  isMuted?: (row: T) => boolean;
}

/** Build + download a styled `.xlsx` with a filled header + frozen header row. */
export async function downloadTableXlsx<T>(
  columns: ExportColumn<T>[],
  rows: T[],
  options: XlsxOptions<T>,
): Promise<void> {
  const ExcelJS = (await import("exceljs")).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = "Flint";
  const ws = wb.addWorksheet(options.sheetName, { views: [{ state: "frozen", ySplit: 1 }] });

  ws.columns = columns.map((c) => ({
    header: c.header,
    width: c.money ? 18 : Math.min(Math.max(c.header.length + 4, 12), 32),
  }));

  const headerRow = ws.getRow(1);
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF0F172A" } };
    cell.alignment = { vertical: "middle", horizontal: "left" };
  });

  rows.forEach((r) => {
    const row = ws.addRow(columns.map((c) => c.xlsx(r)));
    columns.forEach((c, i) => {
      if (c.money) row.getCell(i + 1).numFmt = INR_EXCEL_FORMAT;
    });
    if (options.isMuted?.(r)) {
      row.eachCell((cell) => {
        cell.font = { color: { argb: "FF94A3B8" } };
      });
    }
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  triggerDownload(blob, options.fileName);
}

/** Download server-rendered PDF bytes (base64 over the JSON wire) as a file. */
export function downloadBase64Pdf(pdfBase64: string, fileName: string): void {
  const binary = atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  triggerDownload(new Blob([bytes], { type: "application/pdf" }), fileName);
}

/** Convenience: paise string → rupee number for money columns. */
export function paiseToRupeeNumber(paise: string): number {
  return Number(BigInt(paise)) / 100;
}
